import { signal } from "../net/signal";

/**
 * صداهای میز — همان فایل‌های اپ (MobileGame/Assets/Resources/Sounds)، mp3 شده در
 * public/sfx. با Web Audio پخش می‌شوند تا زمان‌بندی دقیق باشد (برخوردِ تاس با
 * تخته درست همان لحظه‌ای که تاس روی صفحه فرود می‌آید) و چند صدا روی هم بنشینند.
 *
 * مرورگر تا اولین لمسِ آدم صدا نمی‌دهد؛ تا آن‌وقت صدا بی‌خبر رد می‌شود و بعدِ
 * اولین لمس خودش راه می‌افتد. کاربر می‌تواند از سربرگ خاموشش کند (در مرورگر
 * می‌مانَد).
 */
/**
 * dice_throw: صدای تاسِ هر سه بازیِ تاس‌دار (tools/mench_sfx.py) — یک پرتابِ کاملِ ضبط‌شده از «Casino Audio» ِ Kenney
 * (CC0): فرود، دو جهش و غلتِ نرمِ آخر. mn_step: «تُک» ِ گامِ مهره‌ی منچ.
 * nd_place_*، dz_drop_* و dm_place: نشستنِ مهره‌ی تخته‌نرد روی خانه، افتادنِ مهره‌ی دوز در ستون (هر کدام چهار گونه) و
 * سنگِ دومینو روی میز (tools/board_sfx.py) — از Freesound، CC0: مهره‌ی چوبی روی صفحه‌ی چوبی (555190 ِ taure)، بازیِ
 * چهارتایی (447529 ِ boosmyller) و putOnBoard ِ دومینو (745030 ِ poenia).
 */
export type Sfx = "dice_throw" | "move" | "mn_step"
  | "nd_place_1" | "nd_place_2" | "nd_place_3" | "nd_place_4" | "dz_drop_1" | "dz_drop_2" | "dz_drop_3" | "dz_drop_4"
  | "dm_place";
/**
 * صداهای ساختنی (بی فایل): هیسِ مار، برقِ شمشیر، زنگِ برداشتنِ جادو، ووشِ جابه‌جایی، تیکِ میخِ گردونه، شادیِ
 * جایزه، و برای مافیا: تیرِ شب، ضربِ مهر و زنگِ گرداننده سرِ شب.
 */
export type Synth = "hiss" | "slash" | "chime" | "whoosh" | "tick" | "fanfare" | "bang" | "stamp" | "bell" | "pluck";

const KEY = "bazivan.sound";

function load(): boolean {
  try { return localStorage.getItem(KEY) !== "0"; } catch { return true; }
}

export const soundOn = signal<boolean>(load());

export function toggleSound(): void {
  soundOn.value = !soundOn.value;
  try { localStorage.setItem(KEY, soundOn.value ? "1" : "0"); } catch { /* بی‌ذخیره هم کار می‌کند */ }
  if (soundOn.value) void ctx?.resume();
}

/**
 * «صدای تاس» (کاربر، ۲۵ اکتبر ۲۰۲۶: «در صفحه‌ی بازی گزینه‌ای برای خاموش‌کردنِ صدای تاس»): جدا از کلیدِ صدا، در همین
 * مرورگر می‌مانَد. فقط صدای خودِ تاس (diceSfx)؛ مهره و زدن و بقیه می‌مانند.
 * همتای GameSettings.DiceSound ِ اپ.
 */
const DICE_KEY = "bazivan.sound.dice";

export const diceSoundOn = signal<boolean>((() => {
  try { return localStorage.getItem(DICE_KEY) !== "0"; } catch { return true; }
})());

export function toggleDiceSound(): void {
  diceSoundOn.value = !diceSoundOn.value;
  try { localStorage.setItem(DICE_KEY, diceSoundOn.value ? "1" : "0"); } catch { /* بی‌ذخیره هم کار می‌کند */ }
}

/** اولین تقِ dice_throw از آغازِ فایل (میلی‌ثانیه؛ LEAD ِ tools/mench_sfx.py) — همتای SoundFx.DiceHit ِ اپ. */
export const DICE_HIT_MS = 10;

/**
 * صدای تاس (کاربر، ۲۹ اکتبر ۲۰۲۶: «صدای تاس باز خیلی بد است، در همه‌ی بازی‌های تاس‌دار — همه یک صدا»): **یک** فایل
 * برای هر پرتابِ منچ، مارپله و تخته‌نرد، یک بار در هر پرتاب (دو تاس هم)، بی قرعه و بی زیر و بمی — همان SoundFx.PlayDice
 * ِ اپ. پیش‌تر هر تاس تا هشت تکه‌ی ۰٫۱ ثانیه‌ای می‌زد (فرود، جهش، هر قاب، هر غلت) و دو تاس روی هم می‌ترکید.
 * hitAt: میلی‌ثانیه از حالا تا فرودِ تاس — اولین تقِ فایل همان لحظه است. «صدای تاس» خاموش = هیچ.
 */
export function diceSfx(opts: { hitAt?: number; volume?: number } = {}): void {
  if (diceSoundOn.value) sfx("dice_throw", { delay: (opts.hitAt ?? DICE_HIT_MS) - DICE_HIT_MS, volume: opts.volume });
}

let ctx: AudioContext | null = null;
const buffers = new Map<Sfx, Promise<AudioBuffer | null>>();

/** زمینه‌ی صدای مشترکِ میز — menchSfx هم همین را می‌گیرد تا کلیدِ «صدا» و بیدارشدن با اولین لمس یکی باشد. */
export function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    try { ctx = new C(); } catch { return null; }
    const wake = () => { void ctx?.resume(); };
    window.addEventListener("pointerdown", wake, { passive: true });
    window.addEventListener("keydown", wake);
  }
  return ctx;
}

function buffer(name: Sfx): Promise<AudioBuffer | null> {
  let p = buffers.get(name);
  if (!p) {
    const c = audio();
    p = !c ? Promise.resolve(null)
      : fetch(`/sfx/${name}.mp3`).then((r) => r.arrayBuffer()).then((b) => c.decodeAudioData(b)).catch(() => null);
    buffers.set(name, p);
  }
  return p;
}

/**
 * یک صدا، با تأخیرِ دلخواه (میلی‌ثانیه، از همین لحظه). صدای خاموش، مرورگرِ
 * بی‌اجازه یا فایلِ نرسیده = هیچ — هرگز خطا نمی‌دهد.
 */
export function sfx(name: Sfx, opts: { delay?: number; volume?: number; rate?: number } = {}): void {
  if (!soundOn.value) return;
  const c = audio();
  if (!c) return;
  const at = performance.now() + Math.max(0, opts.delay ?? 0);
  void buffer(name).then((b) => {
    // بافتِ صدا هنوز باز نشده (مرورگر منتظرِ لمس است): این یکی رد می‌شود، وگرنه
    // همه‌ی صداهای انباشته بعدِ اولین لمس یک‌جا پخش می‌شدند.
    if (!b || c.state !== "running") return;
    const wait = Math.max(0, at - performance.now()) / 1000;
    const src = c.createBufferSource();
    src.buffer = b;
    src.playbackRate.value = opts.rate ?? 1;
    const g = c.createGain();
    g.gain.value = opts.volume ?? 1;
    src.connect(g).connect(c.destination);
    src.start(c.currentTime + wait);
  });
}

/** بافرها را زودتر بگیر تا اولین تاس بی‌صدا نماند. */
export function preloadSfx(): void {
  for (const n of ["dice_throw", "move", "mn_step",
    "nd_place_1", "nd_place_2", "nd_place_3", "nd_place_4", "dz_drop_1", "dz_drop_2", "dz_drop_3", "dz_drop_4",
    "dm_place"] as Sfx[]) void buffer(n);
}

/**
 * صدای ساختنی با Web Audio — مارپله چهار صدای کوتاه می‌خواست که فایلشان را نداشتیم.
 * همان قاعده‌ی sfx: خاموش، بی‌اجازه یا بی‌پشتیبانی = هیچ.
 */
export function synth(name: Synth, opts: { delay?: number; volume?: number; pitch?: number; step?: number } = {}): void {
  if (!soundOn.value) return;
  const c = audio();
  if (!c || c.state !== "running") return;
  const t0 = c.currentTime + Math.max(0, opts.delay ?? 0) / 1000;
  const vol = opts.volume ?? 1;
  /** بالاتر یا بم‌تر؛ ۱ = خودِ صدا. فلش‌های پشتِ‌هم ووش را پله‌پله بالا می‌برند. */
  const k = opts.pitch ?? 1;
  const out = c.createGain();
  out.gain.value = vol;
  out.connect(c.destination);
  const noise = (dur: number): AudioBufferSourceNode => {
    const b = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource(); src.buffer = b;
    return src;
  };
  const env = (g: GainNode, peak: number, attack: number, dur: number) => {
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  };
  const tone = (f0: number, f1: number, at: number, dur: number, peak: number, type: OscillatorType = "sine") => {
    const o = c.createOscillator(); o.type = type;
    o.frequency.setValueAtTime(f0, t0 + at);
    o.frequency.exponentialRampToValueAtTime(f1, t0 + at + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t0 + at);
    g.gain.exponentialRampToValueAtTime(peak, t0 + at + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
    o.connect(g).connect(out);
    o.start(t0 + at); o.stop(t0 + at + dur + 0.02);
  };
  switch (name) {
    case "hiss": {
      // «هیــس»: نویزِ پرفرکانس با اوجِ آرام و دنباله‌ی بلند
      const src = noise(0.75);
      const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 5200; bp.Q.value = 0.9;
      const g = c.createGain(); env(g, 0.55, 0.08, 0.72);
      src.connect(bp).connect(g).connect(out);
      src.start(t0); src.stop(t0 + 0.75);
      break;
    }
    case "slash": {
      // برقِ شمشیر: ووشِ کوتاه و بعد «دینگ» ِ فلزی
      const src = noise(0.22);
      const hp = c.createBiquadFilter(); hp.type = "highpass";
      hp.frequency.setValueAtTime(900, t0); hp.frequency.exponentialRampToValueAtTime(6000, t0 + 0.18);
      const g = c.createGain(); env(g, 0.5, 0.02, 0.2);
      src.connect(hp).connect(g).connect(out);
      src.start(t0); src.stop(t0 + 0.22);
      tone(2400, 2100, 0.12, 0.45, 0.18, "triangle");
      tone(3600, 3300, 0.12, 0.3, 0.08, "sine");
      break;
    }
    case "chime": {
      // برداشتنِ جادو: دو نتِ بالارونده
      tone(880, 880, 0, 0.25, 0.22, "triangle");
      tone(1318, 1318, 0.09, 0.32, 0.2, "triangle");
      break;
    }
    case "whoosh": {
      const src = noise(0.4);
      const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 1.2;
      bp.frequency.setValueAtTime(400 * k, t0); bp.frequency.exponentialRampToValueAtTime(2600 * k, t0 + 0.35);
      const g = c.createGain(); env(g, 0.45, 0.12, 0.4);
      src.connect(bp).connect(g).connect(out);
      src.start(t0); src.stop(t0 + 0.4);
      break;
    }
    case "tick": {
      // میخِ گردونه به زبانه می‌خورد: تقِ خشکِ کوتاه
      const src = noise(0.03);
      const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 3400; bp.Q.value = 1.6;
      const g = c.createGain(); env(g, 0.4, 0.002, 0.03);
      src.connect(bp).connect(g).connect(out);
      src.start(t0); src.stop(t0 + 0.03);
      tone(2100, 1500, 0, 0.035, 0.05, "triangle");
      break;
    }
    case "fanfare": {
      // جایزه: چهار نتِ بالارونده (دو، می، سل، دو) و آخری کشیده
      [523, 659, 784].forEach((f, i) => tone(f, f, i * 0.09, 0.16, 0.18, "triangle"));
      tone(1047, 1047, 0.27, 0.55, 0.2, "triangle");
      tone(1568, 1568, 0.27, 0.4, 0.05, "sine");
      break;
    }
    case "bang": {
      // تیر: ترقِ نویزِ پهن و دنباله‌ی بمِ کوتاه — نه انفجار؛ میز آرام است
      const src = noise(0.5);
      const lp = c.createBiquadFilter(); lp.type = "lowpass";
      lp.frequency.setValueAtTime(5200, t0); lp.frequency.exponentialRampToValueAtTime(380, t0 + 0.4);
      const g = c.createGain(); env(g, 0.7, 0.003, 0.45);
      src.connect(lp).connect(g).connect(out);
      src.start(t0); src.stop(t0 + 0.5);
      tone(150, 48, 0, 0.32, 0.35, "sine");
      break;
    }
    case "stamp": {
      // مهرِ لاستیکی روی میز: ضربِ بمِ خفه
      const src = noise(0.12);
      const lp = c.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 900;
      const g = c.createGain(); env(g, 0.5, 0.004, 0.12);
      src.connect(lp).connect(g).connect(out);
      src.start(t0); src.stop(t0 + 0.12);
      tone(110, 60, 0, 0.18, 0.3, "sine");
      break;
    }
    case "pluck": {
      // پله‌ی پشتِ‌هم: نتِ کوتاهِ زخمه‌ای روی گامِ پنج‌نتی، هر پله یکی بالاتر (step از ۰)
      const f = PLUCK[Math.min(PLUCK.length - 1, Math.max(0, opts.step ?? 0))]!;
      tone(f, f, 0, 0.2, 0.1, "triangle");
      tone(f * 2, f * 2, 0, 0.09, 0.025, "sine");
      break;
    }
    case "bell": {
      // زنگِ گرداننده: یک نتِ برنجیِ کشیده با فرادستِ ناهماهنگ
      tone(660, 655, 0, 1.4, 0.16, "sine");
      tone(1650, 1640, 0, 0.9, 0.06, "sine");
      tone(2520, 2500, 0, 0.5, 0.03, "triangle");
      break;
    }
  }
}

/** گامِ پنج‌نتیِ «زخمه»، از لا ی چهارم بالا. */
const PLUCK = [440, 494, 554, 659, 740, 880, 988, 1109, 1319, 1480];
